
import { ReactNode, lazy, Suspense } from "react";
import { AuthProvider } from "./AuthProvider";
import ErrorBoundary from "./ErrorBoundary";
import { ThemeProvider } from "next-themes";
import { Toaster } from "./ui/toaster";
// CRITICAL: AnalogHorrorProvider must NOT be lazy loaded
// It provides context that NavigationBar needs immediately
import { AnalogHorrorProvider } from "./dashboard/AnalogHorrorProvider";

// Lazy load CardAwardCelebrationHost to defer framer-motion bundle
const LazyCardAwardCelebrationHost = lazy(() => 
  import("./cards/CardAwardCelebrationHost").then(module => ({ default: module.CardAwardCelebrationHost }))
);

// Lazy load heavy providers that don't need immediate context access
const LazyNotificationProvider = lazy(() => 
  import("./NotificationProvider").then(module => ({ default: module.NotificationProvider }))
);

const LazyOfflineProvider = lazy(() => 
  import("./offline/OfflineContext").then(module => ({ default: module.OfflineProvider }))
);

const LazyPopupProvider = lazy(() => 
  import("./popup-system/PopupProvider").then(module => ({ default: module.PopupProvider }))
);

const LazyGamificationEffectsProvider = lazy(() => 
  import("./effects/GamificationEffectsProvider").then(module => ({ default: module.GamificationEffectsProvider }))
);

const LazyRewardSystemProvider = lazy(() => 
  import("./rewards/RewardSystemProvider").then(module => ({ default: module.RewardSystemProvider }))
);


// Loading fallback for providers
const ProviderLoadingFallback = () => <div style={{ display: 'none' }} />;

interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  return (
    <ErrorBoundary>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <AuthProvider>
          {/* AnalogHorrorProvider is NOT lazy - needed for NavigationBar context */}
          <AnalogHorrorProvider>
            <Suspense fallback={<ProviderLoadingFallback />}>
              <LazyOfflineProvider>
                <LazyNotificationProvider>
                  <LazyGamificationEffectsProvider>
                    <LazyRewardSystemProvider>
                      <LazyPopupProvider>
                        {children}
                        <Toaster />
                        <LazyCardAwardCelebrationHost />
                      </LazyPopupProvider>
                    </LazyRewardSystemProvider>
                  </LazyGamificationEffectsProvider>
                </LazyNotificationProvider>
              </LazyOfflineProvider>
            </Suspense>
          </AnalogHorrorProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
